import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { useShop } from "../lib/shop";
import { useAuth } from "../lib/auth";
import { fmtINR } from "../lib/api";
import { toast } from "sonner";
import { useSEO } from "../lib/useSEO";

export default function Cart() {
  useSEO({ title: "Your Shopping Bag | Joy Pearl", description: "Review the items in your Joy Pearl shopping bag and proceed to checkout with Cash on Delivery.", noIndex: true });
  const { cart, updateCart, removeCart } = useShop();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div className="jp-container py-20 text-center text-brand-muted">Loading…</div>;
  if (!user) return (
    <div className="jp-container py-20 text-center" data-testid="cart-empty">
      <h1 className="font-serif text-3xl mb-3">Sign in to view your bag</h1>
      <Link to="/login" className="jp-btn-primary mt-4 inline-flex">Sign In</Link>
    </div>
  );

  if (!cart.items?.length) return (
    <div className="jp-container py-20 text-center" data-testid="cart-empty">
      <ShoppingBag size={36} className="mx-auto text-brand-muted mb-4" />
      <h1 className="font-serif text-3xl">Your bag is empty</h1>
      <p className="text-brand-muted mt-2">Discover something you'll love.</p>
      <Link to="/products" className="jp-btn-primary mt-6 inline-flex">Browse Shop</Link>
    </div>
  );

  return (
    <div className="jp-container py-8 md:py-12" data-testid="cart-page">
      <h1 className="font-serif text-3xl md:text-5xl mb-8">Shopping Bag</h1>
      <div className="grid md:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-4">
          {cart.items.map((it) => (
            <div key={`${it.product_id}-${it.variant_sku || ""}`} className="bg-white border border-brand-line p-4 flex gap-4" data-testid={`cart-item-${it.product_id}`}>
              <Link to={`/product/${it.slug || it.product_id}`} className="h-24 w-24 sm:h-32 sm:w-32 bg-brand-blush overflow-hidden shrink-0">
                {it.image && <img src={it.image} alt={it.name} className="h-full w-full object-cover"/>}
              </Link>
              <div className="flex-1 flex flex-col">
                <Link to={`/product/${it.slug || it.product_id}`} className="font-medium leading-snug hover:text-brand-primary">{it.name}</Link>
                {it.variant_label && <p className="text-xs text-brand-muted mt-0.5" data-testid="cart-variant-label">{it.variant_label}</p>}
                <p className="text-sm text-brand-ink/80 mt-1">{fmtINR(it.price)}</p>
                <div className="mt-auto flex items-center justify-between pt-3">
                  <div className="inline-flex items-center border border-brand-line">
                    <button onClick={() => updateCart(it.product_id, Math.max(0, it.qty - 1), it.variant_sku)} className="p-2" aria-label="Decrease"><Minus size={14}/></button>
                    <span className="px-3 text-sm">{it.qty}</span>
                    <button onClick={() => updateCart(it.product_id, it.qty + 1, it.variant_sku)} className="p-2" aria-label="Increase"><Plus size={14}/></button>
                  </div>
                  <button onClick={async () => { await removeCart(it.product_id, it.variant_sku); toast.success("Removed"); }} className="text-sm text-brand-muted hover:text-red-700 inline-flex items-center gap-1" data-testid={`cart-remove-${it.product_id}`}>
                    <Trash2 size={14}/> Remove
                  </button>
                </div>
              </div>
              <div className="text-right font-medium hidden sm:block">{fmtINR(it.price * it.qty)}</div>
            </div>
          ))}
        </div>

        <aside className="bg-white border border-brand-line p-6 h-fit sticky top-24" data-testid="cart-summary">
          <h2 className="font-serif text-xl mb-5">Order Summary</h2>
          <div className="text-sm space-y-2">
            <div className="flex justify-between"><span className="text-brand-muted">Subtotal</span><span>{fmtINR(cart.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-brand-muted">Shipping</span><span>{cart.shipping === 0 ? "Free" : fmtINR(cart.shipping)}</span></div>
            {cart.subtotal < cart.free_shipping_over && (
              <p className="text-xs text-brand-muted">Add {fmtINR(cart.free_shipping_over - cart.subtotal)} more for free shipping</p>
            )}
          </div>
          <div className="border-t border-brand-line my-5"/>
          <div className="flex justify-between text-lg font-semibold">
            <span>Total</span><span data-testid="cart-total">{fmtINR(cart.total)}</span>
          </div>
          <button onClick={() => navigate("/checkout")} className="jp-btn-primary w-full mt-6" data-testid="checkout-btn">
            Proceed to Checkout
          </button>
          <Link to="/products" className="block text-center text-sm text-brand-muted mt-4 hover:text-brand-primary">Continue shopping</Link>
        </aside>
      </div>
    </div>
  );
}
